import { Compass, LinkedinLogo, Plugs, Snowflake } from "@phosphor-icons/react";

import RenderIf from "common/components/RenderIf";
import {
  DropdownMenuCheckbox,
  DropdownMenuItem,
} from "common/components/ui/DropdownMenu";
import ProfileImage from "common/components/ui/ProfileImage";
import useCooldownState from "common/datahooks/useCooldownState";
import type { WorkspaceAccount } from "common/types";

import LicenseIcon from "assets/icons/license.svg?react";
import RentedIcon from "assets/icons/plans/avatar-outline-small.svg?react";

interface WorkspaceAccountSelectorItemProps<
  Selection extends string[] | string,
> {
  account: WorkspaceAccount;
  isMultiSelect: boolean;
  selection: Selection;
  setSelection: (selection: Selection) => void;
  onRemoveAccountId: (accountId: string) => void;
  onAddAccountId: (accountId: string) => void;
}

export default function WorkspaceAccountSelectorItem<
  Selection extends string[] | string,
>({
  account,
  isMultiSelect,
  selection,
  setSelection,
  onRemoveAccountId,
  onAddAccountId,
}: WorkspaceAccountSelectorItemProps<Selection>) {
  const {
    id,
    full_name: fullName,
    picture_url: pictureUrl,
    license,
    premium: isPremium,
    sales_navigator: isSalesNavigator,
    state,
    type,
  } = account;

  const { cooldownState, isLoadingCooldownState } = useCooldownState(id);

  const isLoggedIn = state === "LoggedIn";

  function handleCheckboxSelect() {
    if (selection.includes(id)) {
      onRemoveAccountId(id);
      return;
    }
    onAddAccountId(id);
  }

  return (
    <DropdownMenuItem
      onSelect={() => {
        setSelection((typeof selection === "string" ? id : [id]) as Selection);
      }}
    >
      <RenderIf condition={isMultiSelect}>
        <DropdownMenuCheckbox
          isChecked={selection.includes(id)}
          onCheckedChange={handleCheckboxSelect}
        />
      </RenderIf>

      <div className="relative shrink-0">
        <ProfileImage size="xs" src={pictureUrl} />

        {type === "rental" ? (
          <RentedIcon className="absolute -bottom-1 -right-[5px]" />
        ) : (
          <RenderIf condition={!!license}>
            <LicenseIcon className="absolute -bottom-1 -right-[5px]" />
          </RenderIf>
        )}
      </div>

      <span className="line-clamp-1 break-all">{fullName}</span>

      {isLoggedIn ? (
        <>
          <RenderIf condition={isPremium}>
            <LinkedinLogo weight="fill" className="!size-4 fill-yellow-500" />
          </RenderIf>

          <RenderIf condition={isSalesNavigator}>
            <Compass weight="fill" className="!size-4 fill-blue-500" />
          </RenderIf>

          <RenderIf
            condition={!isLoadingCooldownState && cooldownState?.inmail}
          >
            <Snowflake className="!size-4 shrink-0 fill-purple-500" />
          </RenderIf>

          <RenderIf
            condition={!isLoadingCooldownState && cooldownState?.connect}
          >
            <Snowflake className="!size-4 shrink-0 fill-blue-500" />
          </RenderIf>
        </>
      ) : (
        <Plugs weight="fill" className="!size-4 fill-black-400" />
      )}
    </DropdownMenuItem>
  );
}
